import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { InsurerDashboard } from "../components/insurance/InsurerDashboard";
import { insApi, errMsg, INS_TOKEN_KEY } from "../lib/insApi";

const F = ({ label, ...p }) => <label className="block space-y-1"><span className="text-xs font-semibold text-slate-600">{label}</span><Input {...p} /></label>;

const Register = ({ onDone }) => {
  const [f, setF] = useState({ company_name: "", cr_number: "", license_number: "", contact_name: "", phone: "", email: "", password: "", kinds: ["individuals", "cars", "moto"] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const kind = (k) => setF((p) => ({ ...p, kinds: p.kinds.includes(k) ? p.kinds.filter((x) => x !== k) : [...p.kinds, k] }));
  const go = async () => {
    setBusy(true);
    try { await insApi.register(f); toast.success("تم التسجيل — حسابك قيد المراجعة وسنبلغك بالبريد عند الموافقة"); onDone(); }
    catch (e) { toast.error(errMsg(e, "تعذّر التسجيل")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <F label="اسم شركة التأمين / الوسيط" value={f.company_name} onChange={set("company_name")} data-testid="ins-reg-company" />
      <div className="grid grid-cols-2 gap-2"><F label="السجل التجاري" dir="ltr" value={f.cr_number} onChange={set("cr_number")} data-testid="ins-reg-cr" />
        <F label="ترخيص البنك المركزي (اختياري)" dir="ltr" value={f.license_number} onChange={set("license_number")} data-testid="ins-reg-license" /></div>
      <div className="grid grid-cols-2 gap-2"><F label="اسم المسؤول" value={f.contact_name} onChange={set("contact_name")} data-testid="ins-reg-contact" />
        <F label="الجوال" dir="ltr" value={f.phone} onChange={set("phone")} data-testid="ins-reg-phone" /></div>
      <F label="البريد الإلكتروني" type="email" dir="ltr" value={f.email} onChange={set("email")} data-testid="ins-reg-email" />
      <F label="الرمز السري (6 أرقام)" inputMode="numeric" maxLength={6} type="password" dir="ltr" value={f.password} onChange={(e) => set("password")({ target: { value: e.target.value.replace(/\D/g, "").slice(0, 6) } })} data-testid="ins-reg-password" />
      <div className="flex flex-wrap gap-4 text-sm">{[["individuals", "تأمين طبي"], ["cars", "تأمين سيارات"], ["moto", "دراجات نارية"], ["medical", "فحوص طبية"]].map(([k, l]) => (
        <label key={k} className="flex items-center gap-1.5"><Checkbox checked={f.kinds.includes(k)} onCheckedChange={() => kind(k)} data-testid={`ins-reg-kind-${k}`} />{l}</label>))}</div>
      <Button className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={go} data-testid="ins-reg-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} تسجيل كمزود</Button>
    </div>
  );
};

const Forgot = ({ email0, onBack }) => {
  const [email, setEmail] = useState(email0 || "");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const send = () => run(async () => { await insApi.forgot(email); setSent(true); toast.success("إذا كان البريد مسجلاً سيصلك رمز التحقق"); });
  const reset = () => run(async () => { await insApi.reset({ email, code, new_password: pw }); toast.success("تم تغيير كلمة المرور — سجّل الدخول الآن"); onBack(email); });
  return (
    <div className="space-y-3" data-testid="ins-forgot">
      <p className="text-sm text-slate-600">{sent ? "أدخل الرمز المكوّن من 6 أرقام الذي وصلك على البريد، ثم كلمة المرور الجديدة." : "أدخل بريدك المسجّل وسنرسل لك رمز تحقق لاستعادة كلمة المرور."}</p>
      <F label="البريد الإلكتروني" type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} disabled={sent} data-testid="ins-forgot-email" />
      {sent && <>
        <F label="رمز التحقق" dir="ltr" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="ins-forgot-code" />
        <F label="الرمز السري الجديد (6 أرقام)" inputMode="numeric" maxLength={6} type="password" dir="ltr" value={pw} onChange={(e) => setPw(e.target.value.replace(/\D/g, "").slice(0, 6))} data-testid="ins-forgot-newpass" />
      </>}
      <Button className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy || !email.includes("@") || (sent && (code.length !== 6 || pw.length < 8))}
        onClick={sent ? reset : send} data-testid="ins-forgot-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {sent ? "تغيير كلمة المرور" : "إرسال الرمز"}</Button>
      <div className="flex justify-between text-xs">
        <button type="button" onClick={onBack} className="text-slate-500 hover:text-slate-800" data-testid="ins-forgot-back">رجوع لتسجيل الدخول</button>
        {sent && <button type="button" onClick={send} disabled={busy} className="text-emerald-700 hover:underline" data-testid="ins-forgot-resend">إعادة إرسال الرمز</button>}
      </div>
    </div>
  );
};

const Login = ({ onIn }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await insApi.login(f); onIn(); } catch (e) { toast.error(errMsg(e, "تعذّر الدخول")); } finally { setBusy(false); }
  };
  if (forgot) return <Forgot email0={f.email} onBack={(em) => { if (typeof em === "string") setF({ email: em, password: "" }); setForgot(false); }} />;
  return (
    <div className="space-y-3">
      <F label="البريد الإلكتروني" type="email" dir="ltr" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ins-login-email" />
      <F label="كلمة المرور" type="password" dir="ltr" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && go()} data-testid="ins-login-password" />
      <button type="button" onClick={() => setForgot(true)} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="ins-forgot-link">نسيت كلمة المرور؟</button>
      <Button className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} onClick={go} data-testid="ins-login-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} دخول</Button>
    </div>
  );
};

export default function InsurerPortal() {
  const [state, setState] = useState(localStorage.getItem(INS_TOKEN_KEY) ? "in" : "out");
  const [tab, setTab] = useState("login");
  const out = useCallback(() => { localStorage.removeItem(INS_TOKEN_KEY); setState("out"); }, []);
  useEffect(() => { document.title = "WorkZone — مزودو التأمين"; }, []);
  if (state === "in") return <InsurerDashboard onOut={out} />;
  return (
    <div dir="rtl" className="grid min-h-screen place-items-center bg-[#0B3B2E] px-4 py-10 font-body" data-testid="insurer-portal">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center gap-3"><div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50"><ShieldCheck className="h-6 w-6 text-emerald-700" /></div>
          <div><h1 className="font-heading text-xl font-extrabold">بوابة مزودي التأمين</h1><p className="text-xs text-slate-500">استقبل طلبات عروض الأسعار من الشركات على WorkZone</p></div></div>
        <div className="mb-4 grid grid-cols-2 rounded-xl bg-slate-100 p-1 text-sm">
          {[["login", "دخول"], ["register", "تسجيل جديد"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ins-tab-${k}`} className={`rounded-lg py-2 transition-colors ${tab === k ? "bg-white font-bold shadow-sm" : "text-slate-500"}`}>{l}</button>))}
        </div>
        {tab === "login" ? <Login onIn={() => setState("in")} /> : <Register onDone={() => setTab("login")} />}
      </div>
    </div>
  );
}
